// Parcel Detail — full ParcelRecord with tabs for Overview, Power, Cooling,
// Deal Economics, Enrichment History, Alerts.
// Brain: [skills/land-intelligence].
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || '';

const TABS = [
  { id: 'overview', label: 'Overview' },
  // Brian's directive #8 (2026-05-07): Decision Trace tab — the
  // visibility unlock for "why is this score 14.7 vs 8?" The tab
  // consumes GET /api/land/parcels/{id}/trace and renders 6
  // collapsible sections (Discovery → Gates → Infrastructure →
  // Pathways → Convergence/Economics → Outcomes).
  { id: 'trace', label: 'Decision Trace' },
  { id: 'power', label: 'Power Solution Matrix' },
  { id: 'cooling', label: 'Cooling Solution Matrix' },
  { id: 'economics', label: 'Deal Economics' },
  { id: 'outcomes', label: 'Outcomes' },
  { id: 'history', label: 'Enrichment History' },
  { id: 'alerts', label: 'Alerts' },
  // Brian's quality-gate sprint Task 2 (2026-05-08): Equipment Inventory
  // tab — the wire-up path for rapid_deploy. Operators enter generators
  // / transformers from broker listings (HitechSurplus, RB Auctions,
  // MachineryTrader, utility surplus, OEM-cancelled units) so the
  // pathway has real data to bridge with instead of fixture boilerplate.
  { id: 'equipment', label: 'Equipment Inventory' },
];

// Outcome types — kept in sync with otsentry_backend/models/land/outcomes.py:OutcomeType.
// Order is rough deal progression; the pipeline is allowed to branch.
const OUTCOME_TYPES = [
  { id: 'offer_made', label: 'Offer made' },
  { id: 'under_contract', label: 'Under contract' },
  { id: 'closed_won', label: 'Closed (won)' },
  { id: 'closed_lost', label: 'Closed (lost)' },
  { id: 'passed', label: 'Passed (we walked)' },
  { id: 'abandoned', label: 'Abandoned (owner / developer dropped)' },
  { id: 'delayed', label: 'Delayed' },
];

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}


// ── Brian's directive 2026-05-08: Generate Report button handler ────
//
// Fetches the markdown report, opens it in a new tab as a Blob URL,
// and fires the save endpoint so the server keeps an archival copy in
// ~/eosai-reports/. Both surfaces are needed — the inline tab read is
// for working memory; the disk archive is for "read 4-5 of these
// together to assess whether the platform is engineered or templated."

async function generateReport(parcelId, setBusy, setError) {
  setBusy(true);
  setError(null);
  try {
    const resp = await fetch(
      `${API_BASE}/land/parcels/${encodeURIComponent(parcelId)}/report.md`,
      { headers: authHeaders() },
    );
    if (!resp.ok) throw new Error(`report fetch: ${resp.status}`);
    const md = await resp.text();
    // Open in new tab for inline read
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (!win) {
      // Pop-up blocked — offer download via temporary anchor
      const a = document.createElement('a');
      a.href = url;
      a.download = `${parcelId}-${new Date().toISOString().slice(0, 10)}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    // Fire the save endpoint (fire-and-forget — UI doesn't block on it)
    fetch(
      `${API_BASE}/land/parcels/${encodeURIComponent(parcelId)}/report.md/save`,
      { method: 'POST', headers: authHeaders() },
    ).catch((e) => {
      // Save is archival; UI shouldn't error if disk write fails
      console.warn('report save endpoint failed', e);
    });
  } catch (e) {
    setError(`Report generation failed: ${e.message || e}`);
  } finally {
    setBusy(false);
  }
}

export default function ParcelDetail() {
  const { parcelId } = useParams();
  const navigate = useNavigate();
  const [parcel, setParcel] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('overview');
  const [busy, setBusy] = useState(false);
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pResp, aResp, oResp] = await Promise.all([
        fetch(`${API_BASE}/land/parcels/${encodeURIComponent(parcelId)}`, { headers: authHeaders() }),
        fetch(`${API_BASE}/land/alerts?parcel_id=${encodeURIComponent(parcelId)}&include_acknowledged=true`, { headers: authHeaders() }),
        fetch(`${API_BASE}/land/parcels/${encodeURIComponent(parcelId)}/outcomes`, { headers: authHeaders() }),
      ]);
      if (!pResp.ok) throw new Error(`parcel: ${pResp.status}`);
      const pBody = await pResp.json();
      const aBody = aResp.ok ? await aResp.json() : { alerts: [] };
      const oBody = oResp.ok ? await oResp.json() : { outcomes: [] };
      setParcel(pBody);
      setAlerts(aBody.alerts || []);
      setOutcomes(oBody.outcomes || []);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [parcelId]);

  useEffect(() => { load(); }, [load]);

  const reanalyze = async () => {
    setBusy(true);
    setError(null);
    try {
      const resp = await fetch(`${API_BASE}/land/parcels/${encodeURIComponent(parcelId)}/reanalyze`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!resp.ok) throw new Error(`reanalyze: ${resp.status}`);
      await load();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const advanceStage = async (newStage) => {
    setBusy(true);
    try {
      await fetch(`${API_BASE}/land/parcels/${encodeURIComponent(parcelId)}/stage`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ pipeline_stage: newStage }),
      });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete parcel ${parcelId}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const resp = await fetch(`${API_BASE}/land/parcels/${encodeURIComponent(parcelId)}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (resp.status !== 204) throw new Error(`delete: ${resp.status}`);
      navigate('/land-intelligence/pipeline');
    } catch (e) {
      setError(e.message || String(e));
      setBusy(false);
    }
  };

  const toggleLock = async () => {
    if (!parcel) return;
    setBusy(true);
    setError(null);
    try {
      if (parcel.pipeline_stage_locked) {
        // unlock
        const resp = await fetch(
          `${API_BASE}/land/parcels/${encodeURIComponent(parcelId)}/lock`,
          { method: 'DELETE', headers: authHeaders() },
        );
        if (!resp.ok) throw new Error(`unlock: ${resp.status}`);
      } else {
        // lock — prompt for reason
        const reason = window.prompt(
          'Why are you locking this parcel against auto-stage advancement? '
          + '(Optional — e.g. "Owner outreach in progress")',
          '',
        );
        if (reason === null) {
          setBusy(false);
          return;  // cancelled
        }
        const resp = await fetch(
          `${API_BASE}/land/parcels/${encodeURIComponent(parcelId)}/lock`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({
              locked_by: 'Brian',
              reason: reason || null,
            }),
          },
        );
        if (!resp.ok) {
          const detail = (await resp.json().catch(() => ({})))?.detail || resp.statusText;
          throw new Error(detail);
        }
      }
      await load();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading && !parcel) {
    return <div className="p-10 text-center text-sm text-zinc-500">Loading parcel…</div>;
  }
  if (error && !parcel) {
    return (
      <div className="mx-auto max-w-3xl p-10">
        <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
        <Link to="/land-intelligence/pipeline" className="mt-4 inline-block text-sm text-zinc-600 underline">
          ← Back to pipeline
        </Link>
      </div>
    );
  }

  const score = parcel.composite_score ?? 0;
  const scoreColor =
    score >= 70 ? 'bg-emerald-100 text-emerald-800' :
    score >= 50 ? 'bg-amber-100 text-amber-800' :
    'bg-zinc-100 text-zinc-700';

  return (
    <div className="min-h-screen bg-zinc-50 p-6 lg:p-10">
      <div className="mx-auto max-w-6xl">
        <div className="mb-4 flex items-center gap-2 text-sm text-zinc-500">
          <Link to="/land-intelligence/pipeline" className="hover:text-zinc-900">← Pipeline</Link>
        </div>

        <header className="mb-6 rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
                Land Intelligence — Parcel Detail
              </p>
              <h1 className="mt-1 font-mono text-2xl font-bold tracking-tight text-zinc-900">
                {parcel.parcel_id}
              </h1>
              <p className="mt-1 text-sm text-zinc-600">
                {parcel.county}, {parcel.state} · {parcel.acreage?.toFixed(0) ?? '—'} acres ·
                {' '}({parcel.latitude.toFixed(4)}, {parcel.longitude.toFixed(4)})
                {/* Brian's directive 2026-05-08: owner_name on the
                    header so Pipeline Kanban shows the legal-wrapper
                    pattern (LLC / family LP / trust) without a click. */}
                {parcel.owner_name && (
                  <>
                    {' · '}
                    <span className="font-mono text-zinc-700">
                      {parcel.owner_name.length > 50
                        ? parcel.owner_name.slice(0, 50) + '…'
                        : parcel.owner_name}
                    </span>
                  </>
                )}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className={`rounded-full px-3 py-1 text-sm font-bold ${scoreColor}`}>
                Score {score.toFixed(0)}
              </span>
              {/* Brian's directive #2 (2026-05-07): data-completeness
                  badge alongside score — "3 of 12 pathways · 2 of 6
                  power · 1 of 6 cooling". Surfaces the multiplier
                  context for the score so 14.7 vs 60 isn't opaque. */}
              <CompletenessBadge parcel={parcel} />
            </div>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Pipeline stage" value={parcel.pipeline_stage} />
            <Stat
              label="Speed to MW"
              value={parcel.speed_to_mw_months != null ? `${parcel.speed_to_mw_months.toFixed(0)} mo` : '—'}
            />
            <Stat
              label="Revenue acceleration"
              value={parcel.revenue_acceleration_value_usd
                ? `$${formatNumber(parcel.revenue_acceleration_value_usd)}`
                : '—'}
            />
            <Stat
              label="Adjusted power target"
              value={parcel.adjusted_power_target_mw != null
                ? `${parcel.adjusted_power_target_mw.toFixed(0)} MW`
                : 'no convergence shift'}
            />
          </div>

          <div className="mt-5 flex flex-wrap gap-2 border-t border-zinc-200 pt-4">
            <StageAdvancer
              currentStage={parcel.pipeline_stage}
              onAdvance={advanceStage}
              disabled={busy}
            />
            <button
              type="button"
              onClick={reanalyze}
              disabled={busy}
              className="rounded-xl bg-zinc-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-zinc-700 disabled:opacity-50"
            >
              {busy ? 'Reanalyzing…' : 'Re-run convergence'}
            </button>
            <button
              type="button"
              onClick={() => setShowOutcomeModal(true)}
              disabled={busy}
              className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 transition-colors hover:bg-emerald-100 disabled:opacity-50"
              title="Record an outcome event for the compounding loop (Brian's Batch 15.1)"
            >
              + Mark outcome
            </button>
            <button
              type="button"
              onClick={() => generateReport(parcelId, setBusy, setError)}
              disabled={busy}
              className="rounded-xl border border-indigo-300 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-800 transition-colors hover:bg-indigo-100 disabled:opacity-50"
              title="Brian's 2026-05-08 directive: per-parcel markdown report. Opens in a new tab + saves an archival copy to ~/eosai-reports/ on the server."
            >
              Generate Report
            </button>
            <button
              type="button"
              onClick={toggleLock}
              disabled={busy}
              className={
                parcel.pipeline_stage_locked
                  ? 'rounded-xl border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-100 disabled:opacity-50'
                  : 'rounded-xl border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-100 disabled:opacity-50'
              }
              title={
                parcel.pipeline_stage_locked
                  ? `Locked${parcel.pipeline_stage_locked_reason ? ': ' + parcel.pipeline_stage_locked_reason : ''}. Click to unlock.`
                  : 'Lock against auto-stage advancement (Batch 15.2)'
              }
            >
              {parcel.pipeline_stage_locked ? '🔒 Locked' : 'Lock stage'}
            </button>
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="ml-auto rounded-xl border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Delete
            </button>
          </div>

          {error && (
            <div className="mt-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}
        </header>

        <div className="mb-4 flex flex-wrap gap-1 border-b border-zinc-200">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`rounded-t-lg px-4 py-2 text-sm font-semibold transition-colors ${
                tab === t.id
                  ? 'border border-b-white border-zinc-200 bg-white text-zinc-900'
                  : 'text-zinc-500 hover:text-zinc-900'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && <OverviewTab parcel={parcel} />}
        {tab === 'trace' && <DecisionTraceTab parcelId={parcelId} />}
        {tab === 'power' && <PowerMatrixTab matrix={parcel.power_solution_matrix} />}
        {tab === 'cooling' && <CoolingMatrixTab matrix={parcel.cooling_solution_matrix} />}
        {tab === 'economics' && <DealEconomicsTab deal={parcel.deal_economics} />}
        {tab === 'outcomes' && <OutcomesTab outcomes={outcomes} onMark={() => setShowOutcomeModal(true)} />}
        {tab === 'history' && <HistoryTab history={parcel.enrichment_history} />}
        {tab === 'alerts' && <AlertsTab alerts={alerts} />}
        {tab === 'equipment' && <EquipmentInventoryTab parcelId={parcelId} />}

        {showOutcomeModal && (
          <RecordOutcomeModal
            parcelId={parcelId}
            onClose={() => setShowOutcomeModal(false)}
            onRecorded={() => {
              setShowOutcomeModal(false);
              load();
            }}
          />
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</p>
      <p className="mt-1 text-base font-bold text-zinc-900">{value ?? '—'}</p>
    </div>
  );
}

function StageAdvancer({ currentStage, onAdvance, disabled }) {
  const stages = ['discovered', 'screening', 'qualified', 'diligence', 'loi', 'contract', 'closed', 'passed'];
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold text-zinc-500">Move to:</span>
      <select
        value={currentStage}
        onChange={(e) => onAdvance(e.target.value)}
        disabled={disabled}
        className="rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm font-semibold text-zinc-700 outline-none disabled:opacity-50"
      >
        {stages.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
    </div>
  );
}

function OverviewTab({ parcel }) {
  const deal = parcel.deal_economics || {};
  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
      {/* Brian's directive 2026-05-08: Site Facts above Recommendation
          so the evidence base reads first. Tone-coded amber to mark it
          as input data rather than the conclusions. */}
      <SiteFactsSection parcel={parcel} />

      <h2 className="mb-3 text-lg font-bold text-zinc-900">Recommendation</h2>
      <p className="mb-4 leading-relaxed text-zinc-700">
        {deal.recommendation || 'Recommendation will appear after analysis completes.'}
      </p>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Convergence">
          <KV label="Iterations" value={deal.convergence_iterations ?? '—'} />
          <KV label="Initial target" value={deal.target_capacity_mw != null
            ? `${(parcel.power_solution_matrix?.target_mw ?? 0).toFixed(0)} MW initial`
            : '—'} />
          <KV label="Final target" value={`${deal.target_capacity_mw?.toFixed(0) ?? '—'} MW`} />
          <KV label="Power referral" value={parcel.adjusted_power_target_mw != null ? 'yes' : 'no'} />
        </Card>

        <Card title="Headline metrics">
          <KV
            label="Revenue acceleration vs market"
            value={deal.revenue_acceleration_vs_market_usd != null
              ? `$${formatNumber(deal.revenue_acceleration_vs_market_usd)}`
              : '—'}
          />
          <KV
            label="Net development cost (mid)"
            value={deal.net_development_cost_usd?.mid != null
              ? `$${formatNumber(deal.net_development_cost_usd.mid)}`
              : '—'}
          />
          <KV
            label="Time to first MW"
            value={deal.time_to_first_mw_months != null
              ? `${deal.time_to_first_mw_months.toFixed(0)} mo`
              : '—'}
          />
          <KV
            label="Time to full capacity"
            value={deal.time_to_full_capacity_months != null
              ? `${deal.time_to_full_capacity_months.toFixed(0)} mo`
              : '—'}
          />
        </Card>
      </div>

      {deal.market_comparison && (
        <div className="mt-4">
          <Card title={`Comparison vs ${deal.market_comparison.comparison_market}`}>
            <KV label="Reference timeline" value={`${deal.market_comparison.comparison_timeline_months?.toFixed(0)} mo`} />
            <KV label="Reference land cost" value={`$${formatNumber(deal.market_comparison.comparison_land_cost_per_acre)}/ac`} />
            <KV label="Reference power cost" value={`$${deal.market_comparison.comparison_power_cost_mwh?.toFixed(0)}/MWh`} />
            <KV
              label="Timeline advantage"
              value={`${deal.market_comparison.timeline_advantage_months?.toFixed(0)} mo`}
              highlight={deal.market_comparison.timeline_advantage_months > 0 ? 'positive' : 'negative'}
            />
            <KV
              label="Cost advantage"
              value={`$${formatNumber(deal.market_comparison.cost_advantage_usd)}`}
              highlight={deal.market_comparison.cost_advantage_usd > 0 ? 'positive' : 'negative'}
            />
          </Card>
        </div>
      )}
    </section>
  );
}

function PowerMatrixTab({ matrix }) {
  if (!matrix) return <EmptyTab message="No power matrix yet." />;
  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
      <h2 className="mb-3 text-lg font-bold text-zinc-900">
        Power Solution Matrix · target {matrix.target_mw?.toFixed(0)} MW
      </h2>
      <p className="mb-4 text-xs text-zinc-500">
        confidence {(matrix.overall_confidence * 100).toFixed(0)}% · analyzed
        {' '}{new Date(matrix.analyzed_at).toLocaleString()}
      </p>

      <div className="grid gap-4 md:grid-cols-2">
        <RankPanel title="Speed rank — months to first MW" rows={matrix.speed_rank} unit="mo" />
        <RankPanel title="Cost rank — $/MW" rows={matrix.cost_rank} unit="$" />
      </div>

      <PhasedPlan plan={matrix.recommended_strategy} />

      <div className="mt-5 grid gap-3 lg:grid-cols-2">
        <PathwayDetail title="Stranded asset" data={matrix.stranded} />
        <PathwayDetail title="Behind-the-meter" data={matrix.behind_meter} />
        <PathwayDetail title="Rapid deploy" data={matrix.rapid_deploy} />
        <PathwayDetail title="PPA match" data={matrix.ppa} />
        <PathwayDetail title="Grid interconnection" data={matrix.grid} />
        <PathwayDetail title="SMR" data={matrix.smr} />
      </div>

      {matrix.data_gaps?.length > 0 && (
        <CalloutList tone="amber" title={`Data gaps (${matrix.data_gaps.length})`} items={matrix.data_gaps} />
      )}
      {matrix.recommended_next_actions?.length > 0 && (
        <CalloutList tone="emerald" title="Recommended next actions" items={matrix.recommended_next_actions} />
      )}
    </section>
  );
}

function CoolingMatrixTab({ matrix }) {
  if (!matrix) return <EmptyTab message="No cooling matrix yet." />;
  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
      <h2 className="mb-3 text-lg font-bold text-zinc-900">
        Cooling Solution Matrix · {matrix.recommended_strategy}
      </h2>
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Stat label="Recommended technology" value={matrix.recommended_cooling_technology} />
        <Stat
          label="Water risk score"
          value={matrix.water_risk_score != null ? `${matrix.water_risk_score.toFixed(0)} / 100` : '—'}
        />
        <Stat
          label="Power referral"
          value={matrix.power_agent_referral
            ? `→ ${matrix.adjusted_power_target_mw?.toFixed(0)} MW`
            : 'none'}
        />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <PathwayDetail title="Air-cooled" data={matrix.air_cooled} />
        <PathwayDetail title="Groundwater" data={matrix.groundwater} />
        <PathwayDetail title="Municipal supply" data={matrix.municipal} />
        <PathwayDetail title="Reclaimed water" data={matrix.reclaimed} />
        <PathwayDetail title="Liquid cooling" data={matrix.liquid_cooling} />
        <PathwayDetail title="Novel approaches" data={matrix.novel} />
      </div>

      {matrix.data_gaps?.length > 0 && (
        <CalloutList tone="amber" title={`Data gaps (${matrix.data_gaps.length})`} items={matrix.data_gaps} />
      )}
    </section>
  );
}

function DealEconomicsTab({ deal }) {
  if (!deal) return <EmptyTab message="No deal economics yet." />;
  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
      <h2 className="mb-1 text-lg font-bold text-zinc-900">Deal Economics</h2>
      <p className="mb-4 text-sm text-zinc-600">{deal.recommendation}</p>

      <div className="grid gap-4 lg:grid-cols-2">
        <CostTable
          title="Development cost components (low / mid / high)"
          rows={[
            ['Land', deal.land_cost_usd],
            ['Power infrastructure', deal.power_infrastructure_cost_usd],
            ['Cooling infrastructure', deal.cooling_infrastructure_cost_usd],
            ['Building', deal.building_cost_usd],
            ['Site development', deal.site_development_cost_usd],
            ['Fiber', deal.fiber_infrastructure_cost_usd],
            ['Hardening', deal.hardening_cost_usd],
            ['Entitlement', deal.entitlement_cost_usd],
          ]}
          total={deal.total_development_cost_usd}
          totalLabel="Total development"
        />
        <CostTable
          title="Operating costs (annual)"
          rows={[
            ['Power', deal.annual_power_cost_usd],
            ['Water', deal.annual_water_cost_usd],
            ['Property tax', deal.annual_property_tax_usd],
            ['Insurance', deal.annual_insurance_usd],
          ]}
          total={deal.total_annual_opex_usd}
          totalLabel="Total annual opex"
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card title="Incentives + net cost">
          <KV label="Incentive value (mid)" value={`$${formatNumber(deal.total_incentive_value_usd?.mid)}`} />
          <KV label="Net development cost (mid)" value={`$${formatNumber(deal.net_development_cost_usd?.mid)}`} />
          <KV label="Net dev cost (low–high)"
            value={`$${formatNumber(deal.net_development_cost_usd?.low)} – $${formatNumber(deal.net_development_cost_usd?.high)}`} />
        </Card>
        <Card title="Revenue model">
          <KV label="Revenue per kW per month" value={`$${deal.revenue_per_kw_month}`} />
          <KV label="Annual revenue at full capacity"
            value={`$${formatNumber(deal.annual_revenue_at_full_capacity_usd)}`} />
          <KV label="Revenue acceleration vs market"
            value={`$${formatNumber(deal.revenue_acceleration_vs_market_usd)}`}
            highlight={deal.revenue_acceleration_vs_market_usd > 0 ? 'positive' : null} />
        </Card>
      </div>
    </section>
  );
}

function HistoryTab({ history }) {
  if (!history?.length) return <EmptyTab message="No enrichment history yet." />;
  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-lg font-bold text-zinc-900">Enrichment history</h2>
      <ol className="space-y-3">
        {history.slice().reverse().map((entry, i) => (
          <li key={i} className="flex gap-3 border-l-2 border-zinc-200 pl-4">
            <div className="flex-1">
              <p className="text-sm font-semibold text-zinc-900">
                {entry.agent}
                <span className="ml-2 text-xs font-normal text-zinc-500">
                  {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ''}
                </span>
              </p>
              {entry.fields_updated?.length > 0 && (
                <p className="text-xs text-zinc-600">
                  Updated: {entry.fields_updated.join(', ')}
                </p>
              )}
              {entry.data_sources?.length > 0 && (
                <p className="text-xs text-zinc-500">Sources: {entry.data_sources.join(', ')}</p>
              )}
              {entry.note && (
                <p className="mt-1 rounded bg-zinc-50 p-2 text-xs italic text-zinc-700">{entry.note}</p>
              )}
              {entry.confidence != null && (
                <p className="text-xs font-mono text-zinc-500">
                  confidence {(entry.confidence * 100).toFixed(0)}%
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function AlertsTab({ alerts }) {
  if (!alerts.length) return <EmptyTab message="No alerts on this parcel." />;
  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-lg font-bold text-zinc-900">Alerts ({alerts.length})</h2>
      <ul className="space-y-2">
        {alerts.map((a) => (
          <li key={a.alert_id} className={`rounded-xl border p-3 ${
            a.priority === 'critical' ? 'border-red-300 bg-red-50' :
            a.priority === 'high' ? 'border-amber-300 bg-amber-50' :
            'border-zinc-200 bg-zinc-50'
          } ${a.acknowledged ? 'opacity-60' : ''}`}>
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm font-bold text-zinc-900">{a.title}</p>
              <span className="text-xs uppercase tracking-wider text-zinc-500">
                {a.priority} · {a.alert_type}
              </span>
            </div>
            <p className="mt-1 text-sm text-zinc-700">{a.description}</p>
            {a.acknowledged && (
              <p className="mt-2 text-xs italic text-zinc-500">
                ack'd by {a.acknowledged_by || 'unknown'}
                {a.action_taken ? ` — ${a.action_taken}` : ''}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

// Brian's quality-gate sprint Task 2 (2026-05-08): Equipment Inventory.
// Lists current generator + transformer entries the rapid_deploy pathway
// will draw from. Includes a create form (generator OR transformer based
// on type select) and an Archive button per row.
function EquipmentInventoryTab({ parcelId }) {
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState('parcel'); // 'parcel' | 'unassigned' | 'all'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API_BASE}/land/equipment`;
      if (scope === 'parcel') {
        url += `?parcel_id=${encodeURIComponent(parcelId)}`;
      } else if (scope === 'unassigned') {
        url += `?parcel_id=unassigned`;
      }
      const resp = await fetch(url, { headers: authHeaders() });
      if (!resp.ok) throw new Error(`equipment: ${resp.status}`);
      const body = await resp.json();
      setItems(body.equipment || []);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [parcelId, scope]);

  useEffect(() => { load(); }, [load]);

  const archive = async (eqId) => {
    if (!window.confirm('Archive this equipment entry? It can be restored later.')) return;
    try {
      const resp = await fetch(`${API_BASE}/land/equipment/${encodeURIComponent(eqId)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!resp.ok) throw new Error(`archive: ${resp.status}`);
      await load();
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-zinc-900">Equipment Inventory</h2>
          <p className="mt-0.5 text-xs text-zinc-500">
            Manual broker-entry inputs that wire into the rapid_deploy pathway.
            Per Brian's directive, fixture data was retired — entries here are
            the only source of truth until live broker scraping is connected.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="rounded-xl border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-100"
        >
          {showForm ? 'Cancel' : '+ Add equipment'}
        </button>
      </div>

      <div className="mb-3 flex items-center gap-2 text-xs">
        <span className="font-semibold text-zinc-500">Scope:</span>
        {['parcel', 'unassigned', 'all'].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setScope(s)}
            className={`rounded-full border px-3 py-1 font-semibold transition-colors ${
              scope === s
                ? 'border-zinc-900 bg-zinc-900 text-white'
                : 'border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-100'
            }`}
          >
            {s === 'parcel' ? 'This parcel' : s === 'unassigned' ? 'Unassigned' : 'All'}
          </button>
        ))}
      </div>

      {showForm && (
        <EquipmentForm
          parcelId={parcelId}
          onSaved={() => {
            setShowForm(false);
            load();
          }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {error && (
        <div className="mb-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm italic text-zinc-500">Loading…</p>
      ) : items.length === 0 ? (
        <EmptyTab message="No equipment entries in this scope. Use + Add equipment to create one." />
      ) : (
        <ul className="space-y-2">
          {items.map((e) => (
            <li key={e.equipment_id} className="rounded-xl border border-zinc-200 bg-zinc-50 p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <p className="text-sm font-bold text-zinc-900">
                    {e.make} {e.model}
                    <span className="ml-2 rounded-full border border-zinc-300 bg-white px-2 py-0.5 text-xs font-semibold text-zinc-700">
                      {e.equipment_type}
                    </span>
                  </p>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    {e.equipment_type === 'generator'
                      ? `${e.capacity_mw ?? '—'} MW · ${e.fuel_type ?? '—'}`
                      : `${e.capacity_mva ?? '—'} MVA · ${e.voltage_kv ?? '—'} kV`}
                    {' · '}{e.condition} · {e.source}
                    {e.location_state ? ` · ${e.location_state}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => archive(e.equipment_id)}
                  className="rounded-lg border border-zinc-300 bg-white px-3 py-1 text-xs font-semibold text-zinc-700 hover:bg-zinc-100"
                >
                  Archive
                </button>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-zinc-600 sm:grid-cols-4">
                {e.asking_price_usd != null && (
                  <span><span className="text-zinc-400">Ask:</span> ${e.asking_price_usd.toLocaleString()}</span>
                )}
                {e.delivery_estimate_days != null && (
                  <span><span className="text-zinc-400">Delivery:</span> {e.delivery_estimate_days} d</span>
                )}
                {e.broker_name && (
                  <span><span className="text-zinc-400">Broker:</span> {e.broker_name}</span>
                )}
                {e.parcel_id && (
                  <span><span className="text-zinc-400">Parcel:</span> {e.parcel_id}</span>
                )}
              </div>
              {e.notes && (
                <p className="mt-2 text-xs italic text-zinc-600">{e.notes}</p>
              )}
              {e.broker_listing_url && (
                <a
                  href={e.broker_listing_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-block text-xs font-semibold text-blue-700 hover:underline"
                >
                  Source listing →
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function EquipmentForm({ parcelId, onSaved, onCancel }) {
  const [form, setForm] = useState({
    bind_to_parcel: true,
    equipment_type: 'generator',
    make: '',
    model: '',
    capacity_mw: '',
    capacity_mva: '',
    voltage_kv: '',
    fuel_type: 'gas',
    condition: 'used_operational',
    source: 'broker',
    location_state: '',
    location_lat: '',
    location_lon: '',
    asking_price_usd: '',
    delivery_estimate_days: '',
    broker_name: '',
    broker_listing_url: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isGen = form.equipment_type === 'generator';

  const submit = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      parcel_id: form.bind_to_parcel ? parcelId : null,
      equipment_type: form.equipment_type,
      make: form.make.trim(),
      model: form.model.trim(),
      condition: form.condition,
      source: form.source,
      location_state: form.location_state.trim() || null,
      location_lat: form.location_lat === '' ? null : Number(form.location_lat),
      location_lon: form.location_lon === '' ? null : Number(form.location_lon),
      asking_price_usd: form.asking_price_usd === '' ? null : Number(form.asking_price_usd),
      delivery_estimate_days: form.delivery_estimate_days === '' ? null : Number(form.delivery_estimate_days),
      broker_name: form.broker_name.trim() || null,
      broker_listing_url: form.broker_listing_url.trim() || null,
      notes: form.notes.trim() || null,
    };
    if (isGen) {
      payload.capacity_mw = form.capacity_mw === '' ? null : Number(form.capacity_mw);
      payload.fuel_type = form.fuel_type;
    } else {
      payload.capacity_mva = form.capacity_mva === '' ? null : Number(form.capacity_mva);
      payload.voltage_kv = form.voltage_kv === '' ? null : Number(form.voltage_kv);
    }

    try {
      const resp = await fetch(`${API_BASE}/land/equipment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `create: ${resp.status}`);
      }
      onSaved();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const cell = 'rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm';

  return (
    <div className="mb-4 rounded-xl border border-zinc-200 bg-zinc-50 p-4">
      <h3 className="mb-3 text-sm font-bold text-zinc-900">New equipment entry</h3>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex items-center gap-2 text-xs text-zinc-700 sm:col-span-2">
          <input
            type="checkbox"
            checked={form.bind_to_parcel}
            onChange={(e) => set('bind_to_parcel', e.target.checked)}
          />
          Earmark for this parcel ({parcelId}). Uncheck to list nationally.
        </label>

        <label className="text-xs">
          <span className="font-semibold text-zinc-600">Type</span>
          <select className={cell + ' mt-1 w-full'} value={form.equipment_type}
                  onChange={(e) => set('equipment_type', e.target.value)}>
            <option value="generator">Generator</option>
            <option value="transformer">Transformer</option>
          </select>
        </label>

        <label className="text-xs">
          <span className="font-semibold text-zinc-600">Make</span>
          <input className={cell + ' mt-1 w-full'} value={form.make}
                 onChange={(e) => set('make', e.target.value)} placeholder="Caterpillar" />
        </label>
        <label className="text-xs">
          <span className="font-semibold text-zinc-600">Model</span>
          <input className={cell + ' mt-1 w-full'} value={form.model}
                 onChange={(e) => set('model', e.target.value)} placeholder="G3520C" />
        </label>

        {isGen ? (
          <>
            <label className="text-xs">
              <span className="font-semibold text-zinc-600">Capacity (MW)</span>
              <input type="number" step="0.01" className={cell + ' mt-1 w-full'}
                     value={form.capacity_mw} onChange={(e) => set('capacity_mw', e.target.value)} />
            </label>
            <label className="text-xs">
              <span className="font-semibold text-zinc-600">Fuel</span>
              <select className={cell + ' mt-1 w-full'} value={form.fuel_type}
                      onChange={(e) => set('fuel_type', e.target.value)}>
                {['gas', 'diesel', 'dual_fuel', 'solar', 'wind', 'nuclear', 'hydrogen'].map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </label>
          </>
        ) : (
          <>
            <label className="text-xs">
              <span className="font-semibold text-zinc-600">Capacity (MVA)</span>
              <input type="number" step="0.1" className={cell + ' mt-1 w-full'}
                     value={form.capacity_mva} onChange={(e) => set('capacity_mva', e.target.value)} />
            </label>
            <label className="text-xs">
              <span className="font-semibold text-zinc-600">Voltage (kV)</span>
              <input type="number" step="0.1" className={cell + ' mt-1 w-full'}
                     value={form.voltage_kv} onChange={(e) => set('voltage_kv', e.target.value)} />
            </label>
          </>
        )}

        <label className="text-xs">
          <span className="font-semibold text-zinc-600">Condition</span>
          <select className={cell + ' mt-1 w-full'} value={form.condition}
                  onChange={(e) => set('condition', e.target.value)}>
            <option value="new">new</option>
            <option value="used_operational">used_operational</option>
            <option value="decommissioned_functional">decommissioned_functional</option>
          </select>
        </label>
        <label className="text-xs">
          <span className="font-semibold text-zinc-600">Source</span>
          <select className={cell + ' mt-1 w-full'} value={form.source}
                  onChange={(e) => set('source', e.target.value)}>
            <option value="broker">broker</option>
            <option value="auction">auction</option>
            <option value="utility_surplus">utility_surplus</option>
            <option value="military">military</option>
            <option value="oem_cancelled">oem_cancelled</option>
          </select>
        </label>

        <label className="text-xs">
          <span className="font-semibold text-zinc-600">Location state</span>
          <input className={cell + ' mt-1 w-full'} value={form.location_state}
                 onChange={(e) => set('location_state', e.target.value)} placeholder="TX" maxLength={2} />
        </label>
        <label className="text-xs">
          <span className="font-semibold text-zinc-600">Asking price ($)</span>
          <input type="number" className={cell + ' mt-1 w-full'} value={form.asking_price_usd}
                 onChange={(e) => set('asking_price_usd', e.target.value)} />
        </label>

        <label className="text-xs">
          <span className="font-semibold text-zinc-600">Latitude</span>
          <input type="number" step="0.0001" className={cell + ' mt-1 w-full'} value={form.location_lat}
                 onChange={(e) => set('location_lat', e.target.value)} />
        </label>
        <label className="text-xs">
          <span className="font-semibold text-zinc-600">Longitude</span>
          <input type="number" step="0.0001" className={cell + ' mt-1 w-full'} value={form.location_lon}
                 onChange={(e) => set('location_lon', e.target.value)} />
        </label>

        <label className="text-xs">
          <span className="font-semibold text-zinc-600">Delivery (days)</span>
          <input type="number" className={cell + ' mt-1 w-full'} value={form.delivery_estimate_days}
                 onChange={(e) => set('delivery_estimate_days', e.target.value)} />
        </label>
        <label className="text-xs">
          <span className="font-semibold text-zinc-600">Broker name</span>
          <input className={cell + ' mt-1 w-full'} value={form.broker_name}
                 onChange={(e) => set('broker_name', e.target.value)} placeholder="MachineryTrader" />
        </label>

        <label className="text-xs sm:col-span-2">
          <span className="font-semibold text-zinc-600">Listing URL</span>
          <input className={cell + ' mt-1 w-full'} value={form.broker_listing_url}
                 onChange={(e) => set('broker_listing_url', e.target.value)}
                 placeholder="https://…" />
        </label>

        <label className="text-xs sm:col-span-2">
          <span className="font-semibold text-zinc-600">Notes</span>
          <textarea className={cell + ' mt-1 w-full'} rows={2} value={form.notes}
                    onChange={(e) => set('notes', e.target.value)} />
        </label>
      </div>

      {error && (
        <div className="mt-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={saving || !form.make.trim() || !form.model.trim()}
          className="rounded-xl border border-zinc-900 bg-zinc-900 px-4 py-2 text-sm font-semibold text-white hover:bg-zinc-800 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save entry'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded-xl border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-xl border border-zinc-200 p-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">{title}</h3>
      <dl className="space-y-1">{children}</dl>
    </div>
  );
}

function KV({ label, value, highlight }) {
  const cls = highlight === 'positive' ? 'text-emerald-700' :
              highlight === 'negative' ? 'text-red-700' : 'text-zinc-900';
  return (
    <div className="flex justify-between gap-4 text-sm">
      <dt className="text-zinc-600">{label}</dt>
      <dd className={`font-mono font-semibold ${cls}`}>{value ?? '—'}</dd>
    </div>
  );
}

function CostTable({ title, rows, total, totalLabel }) {
  return (
    <div className="rounded-xl border border-zinc-200">
      <h3 className="border-b border-zinc-200 bg-zinc-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        {title}
      </h3>
      <table className="w-full text-sm">
        <tbody>
          {rows.filter((r) => r[1]).map(([label, range]) => (
            <tr key={label} className="border-b border-zinc-100">
              <td className="px-4 py-2 text-zinc-600">{label}</td>
              <td className="px-2 py-2 text-right font-mono text-zinc-700">${formatNumber(range.low)}</td>
              <td className="px-2 py-2 text-right font-mono font-semibold text-zinc-900">${formatNumber(range.mid)}</td>
              <td className="px-4 py-2 text-right font-mono text-zinc-700">${formatNumber(range.high)}</td>
            </tr>
          ))}
          {total && (
            <tr className="bg-zinc-50">
              <td className="px-4 py-2 font-bold text-zinc-900">{totalLabel}</td>
              <td className="px-2 py-2 text-right font-mono text-zinc-700">${formatNumber(total.low)}</td>
              <td className="px-2 py-2 text-right font-mono font-bold text-zinc-900">${formatNumber(total.mid)}</td>
              <td className="px-4 py-2 text-right font-mono text-zinc-700">${formatNumber(total.high)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function RankPanel({ title, rows, unit }) {
  return (
    <div className="rounded-xl border border-zinc-200 p-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">{title}</h3>
      {!rows?.length ? (
        <p className="text-sm italic text-zinc-400">no pathway data yet</p>
      ) : (
        <ol className="space-y-1 text-sm">
          {rows.map((r) => (
            <li key={r.pathway} className="flex justify-between gap-4 text-zinc-700">
              <span><strong>{r.rank}.</strong> {r.pathway}</span>
              <span className="font-mono text-zinc-500">
                {unit === '$' ? `$${formatNumber(r.value)}` : `${r.value.toFixed(0)} ${unit}`}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function PhasedPlan({ plan }) {
  if (!plan?.phases?.length) return null;
  return (
    <div className="mt-4 rounded-xl border border-zinc-200 p-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        Recommended phased deployment
      </h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-200 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">
            <th className="py-2">Phase</th>
            <th className="py-2">Pathway</th>
            <th className="py-2">Capacity</th>
            <th className="py-2">Window</th>
            <th className="py-2">Capex (mid)</th>
          </tr>
        </thead>
        <tbody>
          {plan.phases.map((p) => (
            <tr key={p.phase_number} className="border-b border-zinc-100 text-zinc-700">
              <td className="py-2 font-semibold">{p.phase_number}</td>
              <td className="py-2">{p.name}</td>
              <td className="py-2 font-mono">{p.capacity_mw.toFixed(0)} MW</td>
              <td className="py-2 font-mono text-zinc-500">m{p.start_month}–m{p.completion_month}</td>
              <td className="py-2 font-mono">{formatPhaseCapex(p)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-xs text-zinc-500">
        Total ${formatNumber(plan.total_cost_usd?.mid)} · first MW in {plan.time_to_first_mw_months} mo · full capacity in {plan.time_to_full_capacity_months} mo
      </p>
    </div>
  );
}

function PathwayDetail({ title, data }) {
  if (!data || (Array.isArray(data) && data.length === 0)) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{title}</p>
        <p className="mt-1 text-xs italic text-zinc-400">no data</p>
      </div>
    );
  }
  // Pretty-print the top 4 keys for quick visual scan
  const list = Array.isArray(data) ? data : [data];
  return (
    <div className="rounded-lg border border-zinc-200 p-3">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">{title}</p>
      {list.map((rec, i) => (
        <details key={i} className="text-xs text-zinc-700">
          <summary className="cursor-pointer font-mono text-zinc-900">
            {rec.recommended_pathway || rec.asset_name || rec.utility_name || rec.wwtp_name || rec.aquifer_name || 'record'}
            {' · '}
            <span className="text-zinc-500">conf {((rec.confidence ?? 0) * 100).toFixed(0)}%</span>
          </summary>
          <pre className="mt-2 max-h-64 overflow-auto rounded bg-zinc-50 p-2 text-[11px] leading-tight">
            {JSON.stringify(rec, null, 2)}
          </pre>
        </details>
      ))}
    </div>
  );
}

function CalloutList({ tone, title, items }) {
  const tones = {
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    red: 'border-red-300 bg-red-50 text-red-900',
  };
  return (
    <div className={`mt-4 rounded-xl border px-4 py-3 ${tones[tone] || tones.amber}`}>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider">{title}</p>
      <ul className="space-y-1 text-sm">
        {items.map((it, i) => <li key={i}>• {it}</li>)}
      </ul>
    </div>
  );
}

function EmptyTab({ message }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-zinc-200 bg-white p-10 text-center">
      <p className="text-sm italic text-zinc-500">{message}</p>
    </div>
  );
}

function formatNumber(n) {
  if (n == null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toFixed(0);
}


// ── Outcomes (Batch 15.1 — the compounding loop) ───────────────────


function OutcomesTab({ outcomes, onMark }) {
  if (!outcomes || outcomes.length === 0) {
    return (
      <section className="rounded-2xl border border-zinc-200 bg-white p-6 text-sm text-zinc-600 shadow-sm">
        <p className="text-base font-semibold text-zinc-900">No outcomes recorded.</p>
        <p className="mt-2">
          Mark every meaningful event — offer made, under contract, closed, passed, abandoned —
          so the platform can learn from results. Every parcel without an outcome is unmeasured volume.
        </p>
        <button
          type="button"
          onClick={onMark}
          className="mt-4 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 transition-colors hover:bg-emerald-100"
        >
          + Mark first outcome
        </button>
      </section>
    );
  }

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString();
  };

  const fmtMoney = (n) => {
    if (n == null) return '—';
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
    return `$${n.toFixed(0)}`;
  };

  const TYPE_COLORS = {
    closed_won: 'border-emerald-300 bg-emerald-50',
    closed_lost: 'border-zinc-300 bg-zinc-50',
    passed: 'border-zinc-300 bg-zinc-50 opacity-80',
    abandoned: 'border-zinc-300 bg-zinc-50 opacity-80',
    under_contract: 'border-amber-300 bg-amber-50',
    offer_made: 'border-sky-300 bg-sky-50',
    delayed: 'border-orange-300 bg-orange-50',
  };

  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h3 className="text-lg font-bold text-zinc-900">
          Outcomes — {outcomes.length} recorded
        </h3>
        <button
          type="button"
          onClick={onMark}
          className="rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-800 transition-colors hover:bg-emerald-100"
        >
          + Mark outcome
        </button>
      </div>
      <ul className="space-y-3">
        {outcomes.map((o) => (
          <li
            key={o.outcome_id}
            className={`rounded-xl border p-4 ${TYPE_COLORS[o.outcome_type] || 'border-zinc-200 bg-zinc-50'}`}
          >
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-sm font-bold uppercase tracking-wider text-zinc-900">
                {o.outcome_type.replace(/_/g, ' ')}
              </p>
              <span className="text-xs text-zinc-600">
                {fmtDate(o.occurred_at)}
              </span>
            </div>
            <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-zinc-700">
              {o.counterparty && (<>
                <dt className="font-semibold">Counterparty</dt>
                <dd>{o.counterparty}</dd>
              </>)}
              {o.final_price_usd != null && (<>
                <dt className="font-semibold">Price</dt>
                <dd>{fmtMoney(o.final_price_usd)}</dd>
              </>)}
              {o.recorded_by && (<>
                <dt className="font-semibold">Recorded by</dt>
                <dd>{o.recorded_by}</dd>
              </>)}
              <dt className="font-semibold">Logged at</dt>
              <dd>{fmtDate(o.recorded_at)}</dd>
            </dl>
            {o.reason && (
              <p className="mt-2 text-sm italic text-zinc-700">
                {o.reason}
              </p>
            )}
            {o.notes && (
              <p className="mt-1 text-xs text-zinc-600">
                {o.notes}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}


function RecordOutcomeModal({ parcelId, onClose, onRecorded }) {
  const [outcomeType, setOutcomeType] = useState('offer_made');
  const [reason, setReason] = useState('');
  const [counterparty, setCounterparty] = useState('');
  const [finalPrice, setFinalPrice] = useState('');
  const [recordedBy, setRecordedBy] = useState('Brian');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = {
        outcome_type: outcomeType,
        reason: reason || null,
        counterparty: counterparty || null,
        final_price_usd: finalPrice ? Number(finalPrice) : null,
        recorded_by: recordedBy || null,
        notes: notes || null,
      };
      const resp = await fetch(`${API_BASE}/land/parcels/${encodeURIComponent(parcelId)}/outcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const detail = (await resp.json().catch(() => ({})))?.detail || resp.statusText;
        throw new Error(detail);
      }
      onRecorded();
    } catch (err) {
      setError(err.message || String(err));
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/60 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-lg rounded-2xl border border-zinc-200 bg-white p-6 shadow-xl"
      >
        <h2 className="text-lg font-bold text-zinc-900">Mark outcome</h2>
        <p className="mt-1 text-xs text-zinc-600">
          Append-only — every event becomes part of the parcel's audit trail.
          Terminal outcomes (closed / passed / abandoned) sync the pipeline stage automatically.
        </p>

        {error && (
          <div className="mt-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="text-xs font-semibold text-zinc-700">Outcome type</span>
            <select
              value={outcomeType}
              onChange={(e) => setOutcomeType(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm"
            >
              {OUTCOME_TYPES.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-semibold text-zinc-700">Reason (what drove this outcome?)</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Substation distance turned out wrong; deal economics fell apart"
              rows={2}
              className="mt-1 block w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm"
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-zinc-700">Counterparty (optional)</span>
              <input
                type="text"
                value={counterparty}
                onChange={(e) => setCounterparty(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm"
              />
            </label>

            <label className="block">
              <span className="text-xs font-semibold text-zinc-700">Final price USD (optional)</span>
              <input
                type="number"
                value={finalPrice}
                onChange={(e) => setFinalPrice(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-xs font-semibold text-zinc-700">Recorded by</span>
            <input
              type="text"
              value={recordedBy}
              onChange={(e) => setRecordedBy(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm"
            />
          </label>

          <label className="block">
            <span className="text-xs font-semibold text-zinc-700">Notes (optional)</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 block w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm"
            />
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-xl border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {busy ? 'Recording…' : 'Record outcome'}
          </button>
        </div>
      </form>
    </div>
  );
}


// ── Brian's directive 2026-05-08: Site Facts on Overview tab ────────
//
// Surfaces ownership_data + infrastructure_proximity + climate_risk_data
// + zoning_data above the Recommendation block. Tone-coded amber
// header so it reads as the evidence base feeding the analysis,
// rather than the analysis conclusions.

function SiteFactsSection({ parcel }) {
  const ownership = parcel.ownership_data || {};
  const infra = parcel.infrastructure_proximity || {};
  const climate = parcel.climate_risk_data || {};
  const zoning = parcel.zoning_data || {};
  const physical = parcel.physical_data || {};

  // Task 2b (Brian's quality-gate sprint, 2026-05-08): beneficial-
  // owner + ownership chain. Renders inline below the raw owner_name
  // when OpenCorporates resolved a chain. The "this is the SITE-SPECIFIC
  // line no other platform provides" surface — broker would otherwise
  // need hours of Secretary of State lookups to reach.
  const beneficialOwner = ownership.beneficial_owner || null;
  const entityChain = ownership.entity_chain || [];
  const assemblageCandidates = ownership.assemblage_candidates || [];

  const beneficialOwnerValue = beneficialOwner ? (
    <span>
      <span className="font-bold">{beneficialOwner.company_name}</span>
      {beneficialOwner.formation_date ? <span> · formed {beneficialOwner.formation_date}</span> : null}
      {beneficialOwner.status ? <span> · {beneficialOwner.status}</span> : null}
      {beneficialOwner.registered_agent ? <span><br />agent: {beneficialOwner.registered_agent}</span> : null}
      {(beneficialOwner.officers || []).slice(0, 3).map((o, i) => (
        o?.name ? (
          <span key={i}><br />{o.name}{o.position ? ` — ${o.position}` : ''}</span>
        ) : null
      ))}
      {beneficialOwner.source_url ? (
        <span><br /><a href={beneficialOwner.source_url} target="_blank" rel="noopener noreferrer"
                       className="text-blue-700 hover:underline">OpenCorporates →</a></span>
      ) : null}
    </span>
  ) : '—';

  const ownerName = parcel.owner_name || ownership.owner_name || '—';
  const facts = [
    {
      label: 'Owner',
      value: ownerName,
    },
    // Red Oak validation surfacing (2026-05-08): principal vs manager
    // is the diligence split — render only when different from
    // owner_name to keep the grid tight when there's no addressee.
    ...(ownership.parent_entity && ownership.parent_entity !== ownerName ? [{
      label: 'Principal owner',
      value: ownership.parent_entity,
    }] : []),
    ...(ownership.manager_entity ? [{
      label: 'Manager / operator',
      value: ownership.manager_entity,
    }] : []),
    {
      label: 'Beneficial owner',
      value: beneficialOwnerValue,
    },
    ...(ownership.last_transfer_date ? [{
      label: 'Last transfer',
      value: ownership.last_transfer_date,
    }] : []),
    // Brian's Commit B.3 (2026-05-08): fiber-operator detection signal.
    // Render only when set; detection-only (no gate/score impact).
    ...(ownership.fiber_operator_signal ? [{
      label: 'Fiber-operator owner',
      value: <span className="font-bold text-purple-800">yes — owner_name matches fiber/telecom/network/gigabit</span>,
    }] : []),
    {
      label: 'Owner mailing',
      value: parcel.owner_mailing_address || ownership.owner_mailing_address || '—',
    },
    {
      label: 'Assessed value',
      value: ownership.assessed_value_usd != null
        ? `$${formatNumber(ownership.assessed_value_usd)}`
        : (parcel.assessed_value_usd != null
            ? `$${formatNumber(parcel.assessed_value_usd)}`
            : '—'),
    },
    {
      label: 'Nearest substation',
      value: infra.nearest_substation_id
        ? `${infra.nearest_substation_id} · ${(infra.nearest_substation_distance_mi ?? 0).toFixed(1)} mi${
            infra.nearest_substation_voltage_kv != null
              ? ` · ${infra.nearest_substation_voltage_kv} kV`
              : ''
          }`
        : '—',
    },
    // Brian's Commit C / Task 4 (2026-05-08): on-property transmission.
    // Only render when set — when present, this is THE grid signal.
    ...(infra.transmission_on_property ? [{
      label: 'Transmission on property',
      value: (
        <span className="font-bold text-green-800">
          yes — {infra.transmission_voltage_kv != null ? `${infra.transmission_voltage_kv} kV` : 'power line'}
          {infra.transmission_operator ? ` · ${infra.transmission_operator}` : ''}
        </span>
      ),
    }] : []),
    {
      label: 'Nearest gas pipeline',
      value: infra.nearest_gas_pipeline_id
        ? `${infra.nearest_gas_pipeline_id} · ${(infra.nearest_gas_pipeline_distance_mi ?? 0).toFixed(1)} mi`
        : '—',
    },
    {
      label: 'Nearest fiber splice',
      value: infra.nearest_fiber_splice_distance_mi != null
        ? `${infra.nearest_fiber_splice_distance_mi.toFixed(1)} mi`
        : '—',
    },
    {
      label: 'FEMA flood zone',
      value: physical.flood_zone || climate.flood_zone || parcel.flood_zone || '—',
    },
    {
      label: 'Wetland %',
      value: physical.wetland_pct != null
        ? `${(physical.wetland_pct * 100).toFixed(0)}%`
        : (parcel.wetland_pct != null
            ? `${(parcel.wetland_pct * 100).toFixed(0)}%`
            : '—'),
    },
    {
      label: 'Current zoning',
      value: zoning.current_zoning || parcel.current_zoning || '—',
    },
    {
      label: 'Hypothesis tag',
      value: parcel.hypothesis_tag
        ? <span className="rounded-full border border-blue-300 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-800">{parcel.hypothesis_tag}</span>
        : '—',
    },
  ];

  // Entity chain — officers' other LLCs. Only render the row when
  // there's something to show (keeps the grid tight for parcels with
  // a personal-name owner).
  const relatedTotal = entityChain.reduce(
    (acc, rec) => acc + ((rec.related_companies || []).length), 0,
  );
  if (relatedTotal > 0) {
    facts.push({
      label: 'Entity chain',
      value: (
        <span>
          <span>{relatedTotal} other companies via officers</span>
          {entityChain.slice(0, 3).map((rec, i) => {
            const cs = (rec.related_companies || []).slice(0, 3);
            if (cs.length === 0) return null;
            return (
              <span key={i}>
                <br />via {rec.officer_name}: {cs.map((c) => c.name).join(', ')}
                {(rec.related_companies || []).length > 3 ? ` (+${(rec.related_companies || []).length - 3} more)` : ''}
              </span>
            );
          })}
        </span>
      ),
    });
  }

  if (assemblageCandidates.length > 0) {
    facts.push({
      label: 'Assemblage',
      value: (
        <span>
          <span className="font-bold text-amber-800">{assemblageCandidates.length} other parcel(s) linked via this ownership chain</span>
          {assemblageCandidates.slice(0, 5).map((c, i) => (
            <span key={i}><br /><span className="font-mono text-xs">{c.parcel_id}</span> — {c.county}, {c.state} ({c.acreage} ac) [{c.matched_entity}]</span>
          ))}
        </span>
      ),
    });
  }

  return (
    <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50/40 p-4">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-bold uppercase tracking-wider text-amber-800">
          Site-specific facts feeding the analysis
        </h3>
        <span className="text-xs italic text-amber-700">
          evidence base · NOT the conclusions
        </span>
      </div>
      <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
        {facts.map((f) => (
          <div key={f.label} className="flex gap-2">
            <dt className="w-32 flex-shrink-0 text-xs font-semibold uppercase tracking-wider text-zinc-500">
              {f.label}
            </dt>
            <dd className="flex-1 font-mono text-zinc-800">{f.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}


// ── Brian's directive #4 (2026-05-07): PPA $0 capex display ─────────
//
// PPA pathway has no upfront capex — the deal is an energy contract.
// The literal "$0" reads as a free option vs other pathways with
// real cost structures. Replace with semantic label.

function formatPhaseCapex(phase) {
  const mid = phase?.capex_usd?.mid;
  if (phase?.pathway === 'ppa') {
    // PPA capex is structurally $0 (energy contract, not upfront capex);
    // surface the semantic instead of the literal zero.
    return <span className="text-zinc-500 italic">n/a (energy contract)</span>;
  }
  if (mid == null || mid === 0) {
    return <span className="text-zinc-400">—</span>;
  }
  return `$${formatNumber(mid)}`;
}


// ── Brian's directive #2 (2026-05-07): data-completeness badge ──────
//
// "3 of 12 pathways · 2 of 6 power · 1 of 6 cooling" alongside the
// score. Surfaces WHY a score is what it is — the composite is base
// × completeness, so a low score with low completeness reads as
// "needs more enrichment cycles" rather than "this parcel is bad."

function CompletenessBadge({ parcel }) {
  // Counts derived from power_solution_matrix + cooling_solution_matrix.
  // 6 power slots: grid / stranded / behind_meter / ppa / smr / rapid_deploy.
  // 6 cooling slots: municipal / groundwater / reclaimed / air_cooled /
  //   liquid_cooling / novel.
  const psm = parcel.power_solution_matrix || {};
  const csm = parcel.cooling_solution_matrix || {};

  const powerSlots = ['grid', 'stranded', 'behind_meter', 'ppa', 'smr', 'rapid_deploy'];
  const coolingSlots = ['municipal', 'groundwater', 'reclaimed', 'air_cooled', 'liquid_cooling', 'novel'];

  const powerPopulated = powerSlots.filter((k) => psm[k] != null).length;
  const coolingPopulated = coolingSlots.filter((k) => csm[k] != null).length;
  const total = powerPopulated + coolingPopulated;

  // Color matches score scale — low completeness reads as a flag, not
  // a final judgment.
  const tone = total >= 8
    ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
    : total >= 4
    ? 'border-amber-300 bg-amber-50 text-amber-800'
    : 'border-zinc-300 bg-zinc-50 text-zinc-600';

  return (
    <span
      className={`rounded-full border px-3 py-0.5 text-xs font-semibold ${tone}`}
      title={`Score is base × completeness. ${total} of 12 pathways currently feeding the score.`}
    >
      {total} of 12 pathways · {powerPopulated} of 6 power · {coolingPopulated} of 6 cooling
    </span>
  );
}


// ── Brian's directive #8 (2026-05-07): Decision Trace tab ───────────
//
// Consumes GET /api/land/parcels/{id}/trace (ParcelDecisionTrace JSON
// shape pinned by backend tests in commit b217377). Renders 6
// collapsible sections so Brian can read why a parcel scored what it
// scored — empirical signal-density rather than opaque ranking.
//
// With the relaxed $25M revenue floor (commit 21979f2 onward), more
// small parcels surface in QUALIFIED. The trace is the triage tool —
// Brian inspects each candidate and decides which deserve human
// follow-up. Without the trace he'd be hunting in JSON.

function DecisionTraceTab({ parcelId }) {
  const [trace, setTrace] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await fetch(
          `${API_BASE}/land/parcels/${encodeURIComponent(parcelId)}/trace`,
          { headers: authHeaders() },
        );
        if (!resp.ok) throw new Error(`trace: ${resp.status}`);
        const body = await resp.json();
        if (!cancelled) setTrace(body);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [parcelId]);

  if (loading) return <p className="text-zinc-500">Loading trace…</p>;
  if (error) return <p className="text-red-700">Trace failed: {error}</p>;
  if (!trace) return null;

  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500">
        Assembled at {new Date(trace.assembled_at).toLocaleString()} ·
        schema v{trace.schema_version} · read-only, recomputed from
        current parcel state on each request
      </p>
      <TraceSection title="Discovery" defaultOpen>
        <TraceDiscovery discovery={trace.discovery} identity={trace.identity} />
      </TraceSection>
      <TraceSection title={`Hard gates (${trace.hard_gates.length})`} defaultOpen>
        <TraceGates gates={trace.hard_gates} />
      </TraceSection>
      <TraceSection title="Infrastructure proximity">
        <TraceInfra infra={trace.infrastructure_proximity} />
      </TraceSection>
      <TraceSection title={`Pathways (${trace.pathways.populated_count}/12 populated)`} defaultOpen>
        <TracePathwaysSection pathways={trace.pathways} />
      </TraceSection>
      <TraceSection title="Convergence + Deal Economics + Score">
        <TraceConvergenceEcon
          convergence={trace.convergence}
          deal={trace.deal_economics}
          breakdown={trace.score_breakdown}
        />
      </TraceSection>
      <TraceSection title="Enrichment + Outcomes">
        <TraceEnrichmentOutcomes
          history={trace.enrichment_history}
          outcomes={trace.outcomes}
          competitiveIntel={trace.competitive_intel}
          entityIntel={trace.entity_intel}
          withdrawn={trace.withdrawn_queue}
        />
      </TraceSection>
    </div>
  );
}

function TraceSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-zinc-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <span className="text-sm font-bold text-zinc-900">{title}</span>
        <span className="text-zinc-400">{open ? '▼' : '▶'}</span>
      </button>
      {open && <div className="border-t border-zinc-100 px-4 py-3">{children}</div>}
    </div>
  );
}

function TraceDiscovery({ discovery, identity }) {
  return (
    <dl className="grid gap-2 text-sm sm:grid-cols-2">
      <TraceField label="Discovered via" value={discovery?.discovered_via} />
      <TraceField label="County adapter" value={discovery?.county_adapter} />
      <TraceField
        label="Discovered at"
        value={discovery?.discovered_at ? new Date(discovery.discovered_at).toLocaleString() : '—'}
      />
      <TraceField
        label="Owner at discovery"
        value={discovery?.owner_name_at_discovery ?? '—'}
      />
      <TraceField
        label="Auto-advanced to SCREENING"
        value={discovery?.auto_advanced_to_screening ? 'yes' : 'no'}
      />
      <TraceField
        label="Hypothesis tag"
        value={
          identity?.hypothesis_tag
            ? <span className="rounded-full border border-blue-300 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-800">{identity.hypothesis_tag}</span>
            : '—'
        }
      />
      {discovery?.auto_advance_reason && (
        <TraceField
          label="Auto-advance reason"
          value={discovery.auto_advance_reason}
          full
        />
      )}
    </dl>
  );
}

function TraceGates({ gates }) {
  return (
    <ul className="space-y-2 text-sm">
      {gates.map((g) => (
        <li key={g.gate} className="flex items-start gap-3">
          <span className={`mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${
            g.passed
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {g.passed ? '✓' : '✗'}
          </span>
          <div className="flex-1">
            <p className="font-mono text-xs font-semibold text-zinc-900">{g.gate}</p>
            <p className="text-xs text-zinc-600">{g.rationale}</p>
            {g.data && (
              <pre className="mt-1 rounded bg-zinc-50 p-1 font-mono text-[10px] text-zinc-700 overflow-x-auto">
                {JSON.stringify(g.data, null, 2)}
              </pre>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function TraceInfra({ infra }) {
  if (!infra) return <p className="text-zinc-500 text-sm italic">no infrastructure proximity data</p>;
  return (
    <dl className="grid gap-2 text-sm sm:grid-cols-2">
      <TraceField
        label="Nearest substation"
        value={
          infra.nearest_substation_id
            ? `${infra.nearest_substation_id} (${(infra.nearest_substation_distance_mi ?? 0).toFixed(1)} mi)`
            : '—'
        }
      />
      <TraceField
        label="Nearest gas pipeline"
        value={
          infra.nearest_gas_pipeline_id
            ? `${infra.nearest_gas_pipeline_id} (${(infra.nearest_gas_pipeline_distance_mi ?? 0).toFixed(1)} mi)`
            : '—'
        }
      />
      <TraceField
        label="Nearest fiber splice"
        value={infra.nearest_fiber_splice_distance_mi != null ? `${infra.nearest_fiber_splice_distance_mi.toFixed(1)} mi` : '—'}
      />
      <TraceField
        label="Nearest water main"
        value={infra.nearest_water_main_distance_mi != null ? `${infra.nearest_water_main_distance_mi.toFixed(1)} mi` : '—'}
      />
    </dl>
  );
}

function TracePathwaysSection({ pathways }) {
  return (
    <div>
      <p className="mb-2 text-xs text-zinc-500">
        {pathways.populated_count} of {pathways.total_slots} populated
        ({(pathways.completeness_pct * 100).toFixed(0)}%) — multiplier
        applied to base score for the composite_score downstream.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <TracePathwayCol title="Power (6)" slots={pathways.power} />
        <TracePathwayCol title="Cooling (6)" slots={pathways.cooling} />
      </div>
    </div>
  );
}

function TracePathwayCol({ title, slots }) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-zinc-500">{title}</p>
      <ul className="space-y-1 text-xs">
        {slots.map((s) => (
          <li key={s.name} className="flex items-start gap-2">
            <span className={`mt-0.5 inline-block h-3 w-3 flex-shrink-0 rounded-full ${
              s.populated ? 'bg-emerald-500' : 'bg-zinc-300'
            }`} />
            <div>
              <span className="font-mono">{s.name}</span>
              {s.populated && s.confidence != null && (
                <span className="ml-2 text-zinc-500">
                  conf {(s.confidence * 100).toFixed(0)}%
                </span>
              )}
              {s.summary && (
                <span className="ml-2 text-zinc-600">— {s.summary}</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TraceConvergenceEcon({ convergence, deal, breakdown }) {
  return (
    <div className="space-y-3 text-sm">
      {convergence && (
        <dl className="grid gap-2 sm:grid-cols-3">
          <TraceField label="Convergence iterations" value={convergence.iterations} />
          <TraceField
            label="Final target MW"
            value={convergence.final_target_mw != null ? `${convergence.final_target_mw} MW` : '—'}
          />
          <TraceField label="Cooling strategy" value={convergence.cooling_strategy ?? '—'} />
        </dl>
      )}
      {deal && (
        <dl className="grid gap-2 sm:grid-cols-2">
          <TraceField
            label="Net dev cost (mid)"
            value={deal.net_development_cost_mid_usd != null ? `$${formatNumber(deal.net_development_cost_mid_usd)}` : '—'}
          />
          <TraceField
            label="Total dev cost (mid)"
            value={deal.total_development_cost_mid_usd != null ? `$${formatNumber(deal.total_development_cost_mid_usd)}` : '—'}
          />
          <TraceField
            label="Incentive value (mid)"
            value={deal.incentive_value_mid_usd != null ? `$${formatNumber(deal.incentive_value_mid_usd)}` : '—'}
          />
          <TraceField
            label="Revenue acceleration"
            value={deal.revenue_acceleration_vs_market_usd != null ? `$${formatNumber(deal.revenue_acceleration_vs_market_usd)}` : '—'}
          />
          <TraceField
            label="Time to first MW"
            value={deal.time_to_first_mw_months != null ? `${deal.time_to_first_mw_months.toFixed(0)} mo` : '—'}
          />
          <TraceField
            label="Time to full capacity"
            value={deal.time_to_full_capacity_months != null ? `${deal.time_to_full_capacity_months.toFixed(0)} mo` : '—'}
          />
        </dl>
      )}
      <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
          Score breakdown
        </p>
        <p className="mt-1 font-mono text-sm text-zinc-800">
          base_score {breakdown.base_score != null ? breakdown.base_score.toFixed(1) : '?'} ×
          completeness_pct {breakdown.completeness_pct != null ? breakdown.completeness_pct.toFixed(2) : '?'}
          {' = '}
          composite_score {breakdown.composite_score != null ? breakdown.composite_score.toFixed(1) : '?'}
        </p>
        <p className="mt-1 text-xs text-zinc-600">
          Pinned: composite_score = (confidence × 100) × ({breakdown.populated_pathways ?? '?'}/12).
          Brian's 2026-05-07 multiplier — incomplete data gets a smaller score.
        </p>
      </div>
    </div>
  );
}

function TraceEnrichmentOutcomes({
  history, outcomes, competitiveIntel, entityIntel, withdrawn,
}) {
  return (
    <div className="space-y-3 text-sm">
      {withdrawn && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-amber-700">
            Withdrawn-queue signal (inversion thesis)
          </p>
          <p className="mt-1">
            <strong>{withdrawn.withdrawn_mw_within_25mi ?? 0} MW</strong> from{' '}
            <strong>{withdrawn.withdrawn_project_count_within_25mi ?? 0}</strong>{' '}
            withdrawn interconnection projects within 25 mi
          </p>
        </div>
      )}
      {competitiveIntel && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-blue-700">
            Competitive intelligence
          </p>
          <p className="mt-1 font-mono text-xs">{JSON.stringify(competitiveIntel)}</p>
        </div>
      )}
      {entityIntel && (
        <div className="rounded-lg border border-purple-200 bg-purple-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-purple-700">
            OpenCorporates entity intel
          </p>
          <p className="mt-1 text-xs">
            Best match: <strong>{entityIntel.best_match?.name ?? '?'}</strong>{' '}
            ({entityIntel.jurisdiction ?? '?'})
            {entityIntel.match_count != null && ` — ${entityIntel.match_count} candidate(s)`}
          </p>
          {entityIntel.best_match?.source_url && (
            <a
              className="mt-1 inline-block text-xs text-purple-700 underline"
              href={entityIntel.best_match.source_url} target="_blank" rel="noreferrer"
            >View on OpenCorporates →</a>
          )}
        </div>
      )}
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-zinc-500">
          Enrichment history ({history.length})
        </p>
        <ul className="space-y-1 text-xs">
          {history.slice(0, 10).map((h, i) => (
            <li key={i} className="border-l-2 border-zinc-200 pl-3">
              <span className="font-mono font-semibold">{h.agent}</span>
              {h.timestamp && (
                <span className="ml-2 text-zinc-400">
                  {new Date(h.timestamp).toLocaleString()}
                </span>
              )}
              {h.fields_updated?.length > 0 && (
                <span className="ml-2 text-zinc-600">
                  → {h.fields_updated.join(', ')}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-zinc-500">
          Outcomes ({outcomes.length})
        </p>
        {outcomes.length === 0 ? (
          <p className="text-xs italic text-zinc-400">no outcome events recorded</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {outcomes.map((o, i) => (
              <li key={i} className="border-l-2 border-emerald-300 pl-3">
                <span className="font-mono font-semibold">{o.outcome_type}</span>
                {o.closed_at && (
                  <span className="ml-2 text-zinc-400">
                    {new Date(o.closed_at).toLocaleString()}
                  </span>
                )}
                {o.actual_net_dev_cost_usd != null && (
                  <span className="ml-2">${formatNumber(o.actual_net_dev_cost_usd)}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function TraceField({ label, value, full = false }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <dt className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</dt>
      <dd className="mt-0.5 font-mono text-zinc-800">{value ?? '—'}</dd>
    </div>
  );
}
